/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext } from 'react'

// @mui core components
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import HelpIcon from '@mui/icons-material/Help'

// Settings page components
import FormField from 'components/FormField'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import OnboardingMultiSelectTile from 'components/OnboardingMultiSelectTile'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

function MilitaryDetails() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = () => {
    updateUser()
    toast.info('You have successfully updated your military details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  const showMilitary = intakeState?.military
  const showMilitaryDates = intakeState?.military
  const showMilitaryReserves = intakeState?.militaryReserves

  return (
    <Card
      sx={{
        textAlign: 'left',
        boxShadow: 0,
        border: '1px solid #ddd',
        borderRadius: 2,
      }}
    >
      <Box px={3} pt={3} display="flex" justifyContent="space-between">
        <Typography variant="h3" fontSize={{ xs: '24px', md: '30px' }}>
          Military
        </Typography>

        <Button
          variant="gradient"
          color="dark"
          sx={{ height: '100%' }}
          onClick={onSubmit}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
      </Box>
      <Box component="form" pb={3} px={3}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'military',
              label: 'Did you serve in the military?',
              placeholder: '',
              inputType: 'select',
              value: intakeState.military,
              onChange: updateIntakeStateAndSubmit((military) =>
                updateIntakeStateAtKey({ military })
              ),
              options: selectData.yesNo,
            })}
          </Grid>
          {showMilitary ? (
            <>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'militaryBranch',
                  label: 'Military Branch',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.militaryBranch,
                  onChange: updateIntakeStateAndSubmit((militaryBranch) =>
                    updateIntakeStateAtKey({ militaryBranch })
                  ),
                  options: selectData.militaryBranch,
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'militaryActiveDutyPension',
                  label: 'Are you collecting an active duty pension?',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.militaryActiveDutyPension,
                  onChange: updateIntakeStateAndSubmit(
                    (militaryActiveDutyPension) =>
                      updateIntakeStateAtKey({ militaryActiveDutyPension })
                  ),
                  options: selectData.yesNo,
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container columnSpacing={3} rowSpacing={2} pt={1}>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'militaryActiveDutyStartDate',
                      label: 'Active Duty Start Date',
                      inputType: 'date',
                      value: intakeState.militaryActiveDutyStartDate,
                      onChange: updateIntakeStateAndSubmit(
                        (militaryActiveDutyStartDate) =>
                          updateIntakeStateAtKey({
                            militaryActiveDutyStartDate,
                          })
                      ),
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'militaryActiveDutyEndDate',
                      label: 'Active Duty Discharge Date',
                      inputType: 'date',
                      value: intakeState.militaryActiveDutyEndDate,
                      onChange: updateIntakeStateAndSubmit(
                        (militaryActiveDutyEndDate) =>
                          updateIntakeStateAtKey({
                            militaryActiveDutyEndDate,
                          })
                      ),
                    })}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'militaryReserves',
                  label: 'Were you in the reserves?',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.militaryReserves,
                  onChange: updateIntakeStateAndSubmit((militaryReserves) =>
                    updateIntakeStateAtKey({ militaryReserves })
                  ),
                  options: selectData.yesNo,
                })}
              </Grid>
              {showMilitaryReserves && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Grid container columnSpacing={3} rowSpacing={2} pt={1}>
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'militaryReservesStartDate',
                          label: 'Reserves Start Date',
                          inputType: 'date',
                          intakeAttribute:
                            intakeState.militaryReservesStartDate,
                          onChange: updateIntakeStateAndSubmit(
                            (militaryReservesStartDate) =>
                              updateIntakeStateAtKey({
                                militaryReservesStartDate,
                              })
                          ),
                        })}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'militaryReservesEndDate',
                          label: 'Reserves Discharge Date',
                          inputType: 'date',
                          value: intakeState.militaryReservesEndDate,
                          onChange: updateIntakeStateAndSubmit(
                            (militaryReservesEndDate) =>
                              updateIntakeStateAtKey({
                                militaryReservesEndDate,
                              })
                          ),
                        })}
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'militaryPaidDeposit',
                  label:
                    'Have you paid a deposit to your agency for your active duty time?',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.militaryPaidDeposit,
                  onChange: updateIntakeStateAndSubmit((militaryPaidDeposit) =>
                    updateIntakeStateAtKey({
                      militaryPaidDeposit,
                    })
                  ),
                  options: selectData.yesNo,
                })}
              </Grid>
            </>
          ) : (
            <></>
          )}
        </Grid>
      </Box>
    </Card>
  )
}

export default MilitaryDetails
